interface CurrencyFormatterOptions {
  locale?: string;
  currencyDisplay?: "symbol" | "narrowSymbol" | "code" | "name";
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}

/**
 * Formats a number as currency with proper localization
 * @param amount - The number to format
 * @param currency - ISO 4217 currency code (e.g., 'USD', 'EUR', 'NGN')
 * @param options - Additional formatting options
 * @returns Formatted currency string
 */
export const formatCurrency = (
  amount: number,
  currency: string,
  options: CurrencyFormatterOptions = {}
): string => {
  // Default options
  const defaultOptions = {
    locale: getDefaultLocale(currency),
    currencyDisplay: "symbol",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...options,
  };

  // Handle special cases for currencies without decimals
  if (["JPY", "KRW", "CLP"].includes(currency)) {
    defaultOptions.minimumFractionDigits = 0;
    defaultOptions.maximumFractionDigits = 0;
  }

  try {
    return new Intl.NumberFormat(defaultOptions.locale, {
      style: "currency",
      currency,
      currencyDisplay: defaultOptions.currencyDisplay as
        | "symbol"
        | "narrowSymbol"
        | "code"
        | "name"
        | undefined,
      minimumFractionDigits: defaultOptions.minimumFractionDigits,
      maximumFractionDigits: defaultOptions.maximumFractionDigits,
    }).format(amount);
  } catch (error) {
    // console.error("Currency formatting error:", error);
    // Fallback formatting
    return `${currency} ${amount.toFixed(
      defaultOptions.maximumFractionDigits
    )}`;
  }
};

/**
 * Gets default locale based on currency
 */
const getDefaultLocale = (currency: string): string => {
  const currencyLocales: Record<string, string> = {
    USD: "en-US",
    EUR: "de-DE",
    GBP: "en-GB",
    NGN: "en-NG",
    JPY: "ja-JP",
    KRW: "ko-KR",
    CNY: "zh-CN",
    INR: "en-IN",
    BRL: "pt-BR",
    // Add more currency-locale mappings as needed
  };

  return currencyLocales[currency] || "en-US";
};
